import { __decorate, __metadata } from 'tslib';
import React, { Component } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './drawer.less.js';
import { isDraggableOptions, DrawerType, DrawerAnimation } from '../../services/drawer/drawer-info.js';
import { DrawerService } from '../../services/drawer/drawer-service.js';
import { Widget } from '../widget/widget.js';
import { DraggableEdge } from './draggable-edge.js';
import { Inject } from '@tencent/ioc';
import { limit } from '../../utils/math-utils.js';
import { evaluate } from '../../controllers/base/evaluate.js';

class Drawer extends Component {
    constructor() {
        super(...arguments);
        this.transitionRef = React.createRef();
        this.startWidth = 0;
        this.setDrawerRef = (drawerDom) => {
            this.drawerDom = drawerDom;
            this.props.forwardRef(drawerDom);
        };
        this.onDrawerEdgeDragStart = () => {
            var _a, _b;
            this.startWidth = (_b = (_a = this.drawerDom) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 280;
        };
        this.onDrawerEdgeDrag = (newWidth) => {
            const { minWidth = 280, maxWidth = 480 } = isDraggableOptions(this.props.edgeDraggable)
                ? this.props.edgeDraggable
                : {};
            const width = limit(newWidth, minWidth, maxWidth);
            this.drawerService.updateCurrentDrawer(this.props.align, {
                style: Object.assign(Object.assign({}, this.props.style), { width }),
            }, this.props.name);
            this.width = width;
        };
        this.onDrawerEdgeDragEnd = () => {
            var _a, _b;
            if (this.width && isDraggableOptions(this.props.edgeDraggable)) {
                (_b = (_a = this.props.edgeDraggable).onDragEnd) === null || _b === void 0 ? void 0 : _b.call(_a, this.width);
            }
        };
    }
    getWrapperClasses() {
        const drawerClasses = [styles.drawer, styles[`drawer-${this.props.align}`]];
        if (this.transitionRef.current) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const applied = this.transitionRef.current['appliedClasses'];
            for (const status of Object.values(applied)) {
                for (const classname of Object.values(status)) {
                    drawerClasses.push(classname);
                }
            }
        }
        // 默认开启动画
        if (!this.props.animationDisabled) {
            drawerClasses.push(styles['animation-transform']);
        }
        if (this.props.type === DrawerType.TRANSPARENT
            || (!this.props.opened && this.type === DrawerType.TRANSPARENT)) {
            drawerClasses.push(styles.transparent);
        }
        if (this.props.animation === DrawerAnimation.FADE_IN
            || (!this.props.opened && this.animation === DrawerAnimation.FADE_IN)) {
            drawerClasses.push(styles.fade);
            // 默认开启动画
            if (!this.props.animationDisabled) {
                drawerClasses.push(styles['animation-fade']);
            }
        }
        return drawerClasses;
    }
    getDrawerWidget(visible, content, name) {
        if (!content) {
            return null;
        }
        return React.createElement(Widget, { style: { maxHeight: '100%', overflow: 'auto', height: '100%' }, key: `drawer-${this.props.align}-${name}`, visible: visible }, content);
    }
    getContents() {
        var _a, _b, _c;
        const hiddenContents = (_c = [...(_b = (_a = this.props.hiddenContents) === null || _a === void 0 ? void 0 : _a.values()) !== null && _b !== void 0 ? _b : []]) === null || _c === void 0 ? void 0 : _c.map(widget => this.getDrawerWidget(false, widget.content, widget.name));
        const content = this.getDrawerWidget(true, this.props.content, this.props.name);
        return [...hiddenContents, content];
    }
    render() {
        const { align, type, animation, opened, edgeDraggable, style, animationDisabled, showCloseButton: showClose, title, titleStyle, closeButtonStyle, persistContents, usePersistContent, customHeaderButtonsConfig, onBack, onDrawerClose, onEnter, onEntered, onExit, onExited, } = this.props;
        const drawerClasses = this.getWrapperClasses();
        this.type = type !== null && type !== void 0 ? type : DrawerType.PANEL;
        this.animation = animation !== null && animation !== void 0 ? animation : DrawerAnimation.SLIDE_IN;
        const showCloseButton = showClose !== null && showClose !== void 0 ? showClose : title;
        return (React.createElement(CSSTransition, { in: opened, timeout: animationDisabled ? 0 : 300, appear: true, classNames: {
                appear: styles['before-open'],
                appearActive: styles.opening,
                appearDone: styles['after-open'],
                enter: styles['before-open'],
                enterActive: styles.opening,
                enterDone: styles['after-open'],
                exit: styles['before-close'],
                exitActive: styles.closing,
                exitDone: styles['after-close'],
            }, onEnter: onEnter, onEntered: onEntered, onExit: onExit, onExited: onExited, ref: this.transitionRef },
            React.createElement("div", { ref: ref => this.setDrawerRef(ref), id: `workbench-drawer-${align}`, className: drawerClasses.join(' '), style: style, role: "complementary", "aria-roledescription": "sidebar", "aria-labelledby": `drawer-title-${align}` },
                edgeDraggable && React.createElement(DraggableEdge, { align: align, onDrag: this.onDrawerEdgeDrag, onDragStart: this.onDrawerEdgeDragStart, onDragEnd: this.onDrawerEdgeDragEnd }),
                title && React.createElement("div", { id: `drawer-title-${align}`, className: styles['drawer-title'], style: titleStyle },
                    onBack ? React.createElement("div", { className: styles['drawer-back-button'], onClick: onBack }) : null,
                    evaluate(title, null)),
                React.createElement("div", { className: styles['custom-buttons-container'] }, customHeaderButtonsConfig === null || customHeaderButtonsConfig === void 0 ? void 0 : customHeaderButtonsConfig.map((cfg) => {
                    const { icon, name, ariaLabel = name, onClick, style: customStyle, } = cfg;
                    return React.createElement("div", { style: Object.assign(Object.assign({}, customStyle), { backgroundImage: `url(${icon})` }), className: styles['custom-button'], key: name, role: "button", "aria-label": ariaLabel, onClick: onClick });
                })),
                showCloseButton && React.createElement("div", { className: styles['drawer-close-button'], role: "button", "aria-label": "Close sidebar", style: closeButtonStyle, onClick: () => {
                        onDrawerClose();
                    } }),
                React.createElement("div", { id: `drawer-${align}-persist`, style: {
                        height: '100%',
                        display: usePersistContent ? 'block' : 'none',
                    } }, (persistContents !== null && persistContents !== void 0 ? persistContents : []).map(widget => React.createElement(Widget, { style: { maxHeight: '100%', overflow: 'auto', height: '100%' }, key: `drawer-${align}-persist-${widget.name}`, visible: widget.name === usePersistContent }, widget.content))),
                usePersistContent ? null : (this.getContents() || null))));
    }
}
__decorate([
    Inject(),
    __metadata("design:type", DrawerService)
], Drawer.prototype, "drawerService", void 0);

export { Drawer };
//# sourceMappingURL=drawer.js.map
